import { useMemo, useState } from 'react';
import { Checkbox, Dialog, Input } from '@arco-design/mobile-react';
import { IconApps, IconClockCircle, IconMessage, IconPlus, IconRight, IconSettings, IconSearch } from '../components/icons';
import { mobileMessage } from '../components/mobileFeedback';
import type { MobileBusinessApp, MobileOperation } from '../types';

type Props = {
  name: string;
  apps: MobileBusinessApp[];
  counts?: { todo: number; urgent: number; cc: number };
  loading: boolean;
  error?: string;
  onRetry: () => void;
  catalog: MobileOperation[];
  catalogLoading: boolean;
  catalogHasMore: boolean;
  configError?: string;
  onConfigure: () => void;
  onSearch: (keyword: string) => void;
  onLoadMore: () => void;
  onReloadFavorites: () => Promise<void>;
  operations: MobileOperation[];
  favoriteIds: string[];
  onFavoriteChange: (ids: string[]) => Promise<boolean>;
  onOpenTasks: (group?: string) => void;
  onOpenApps: () => void;
  onOpenApp: (appCode: MobileBusinessApp['code']) => void;
  onOpenOperation: (operation: MobileOperation) => void;
};

function MobileWorkbenchView(props: Props) {
  const [favoriteOpen, setFavoriteOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [favoriteDraft, setFavoriteDraft] = useState(props.favoriteIds);
  const [keyword, setKeyword] = useState('');
  const favorites = useMemo(() => props.favoriteIds.map((id) => props.operations.find((item) => item.id === id)).filter((item): item is MobileOperation => Boolean(item)), [props.favoriteIds, props.operations]);
  const favoriteApps = props.apps;
  const todo = props.counts?.todo;
  const urgent = props.counts?.urgent;
  const ccUnread = props.counts?.cc;
  const searchOperations = props.catalog;

  const openFavoriteConfig = () => {
    setFavoriteDraft(props.favoriteIds);
    setKeyword('');
    setFavoriteOpen(true);
    props.onConfigure();
  };


  return <div className="mobile-page mobile-workbench">
    <section className="mobile-workbench__hero">
      <h2>{props.name}，下午好</h2>
      <p>优先处理催办事项，及时查看抄送和通知。</p>
    </section>

    {props.loading ? <div role="status">正在加载工作摘要...</div> : null}
    {props.error ? <div role="alert">{props.error}<button type="button" onClick={props.onRetry}>重试</button></div> : null}
    <section className="mobile-workbench__summary" aria-label="待办和抄送摘要">
      <button className="mobile-summary-row" type="button" onClick={() => props.onOpenTasks('todo')}>
        <IconClockCircle className="mobile-summary-row__icon" />
        <span className="mobile-summary-row__label">待办</span>
        <strong>{todo ?? '—'}</strong>
        <span className="mobile-summary-row__urgent">催办 {urgent ?? '—'}</span>
        <IconRight className="mobile-summary-row__arrow" />
      </button>
      <button className="mobile-summary-row" type="button" onClick={() => props.onOpenTasks('cc')}>
        <IconMessage className="mobile-summary-row__icon" />
        <span className="mobile-summary-row__label">抄送</span>
        <small>未读</small>
        <strong>{ccUnread ?? '—'}</strong>
        <IconRight className="mobile-summary-row__arrow" />
      </button>
    </section>

    <section className="mobile-section mobile-workbench__section">
      <div className="mobile-section__head">
        <h3>常用应用</h3>
        <button className="mobile-text-button" type="button" onClick={props.onOpenApps}>全部 <IconRight /></button>
      </div>
      <div className="mobile-workbench__apps">
        {favoriteApps.map((app) => <button type="button" key={app.code} aria-label={`打开${app.name}`} onClick={() => props.onOpenApp(app.code)}>
          <span className="mobile-workbench__app-icon"><IconApps /></span>
          <strong>{app.name}</strong>
        </button>)}
        <button className="mobile-workbench__app-add" type="button" aria-label="选择授权应用" onClick={props.onOpenApps}>
          <span className="mobile-workbench__app-icon"><IconPlus /></span>
          <strong>选择应用</strong>
        </button>
      </div>
    </section>

    <section className="mobile-section mobile-workbench__section">
      <div className="mobile-section__head">
        <h3>常用操作</h3>
        <button className="mobile-icon-button" type="button" aria-label="配置常用操作" disabled={props.loading || Boolean(props.error)} onClick={openFavoriteConfig}><IconSettings /></button>
      </div>
      <div className="mobile-operation-grid">
        {favorites.map((item) => <button className="mobile-operation" type="button" key={item.id} title={`${item.name} · ${item.source}`} onClick={() => item.enabled ? props.onOpenOperation(item) : mobileMessage.warning(item.unavailableReason || '当前操作暂不可用')}>
          <span className="mobile-operation__icon"><IconApps /></span>
          <strong>{item.name}</strong>
        </button>)}
      </div>
    </section>

    <Dialog title="配置常用操作" visible={favoriteOpen} close={() => { if (!saving) setFavoriteOpen(false); }} footer={[
      { content: '取消', disabled: saving, onClick: () => { if (!saving) setFavoriteOpen(false); return true; } },
      { content: saving ? '保存中...' : '保存', disabled: saving, onClick: async () => { if (saving) return true; setSaving(true); try { if (await props.onFavoriteChange(favoriteDraft)) { setFavoriteOpen(false); mobileMessage.success('常用操作已更新'); } } finally { setSaving(false); } return true; } }
    ]}>
      <Input className="mobile-control-input" border="none" prefix={<IconSearch />} value={keyword} clearable placeholder="搜索操作" onInput={(_, value) => { setKeyword(value); props.onSearch(value); }} />
      {props.configError ? <div role="alert">{props.configError}<button type="button" disabled={saving} onClick={() => void props.onReloadFavorites()}>重载服务端收藏</button></div> : null}
      {props.catalogLoading ? <div role="status">正在加载操作...</div> : null}
      <div className="mobile-favorite-selected" aria-label="已选操作顺序">{favoriteDraft.map((id, index) => <button type="button" disabled={saving} key={id} onClick={() => setFavoriteDraft(current => current.filter(value => value !== id))}>{index + 1}. {props.operations.find(item => item.id === id)?.name ?? '不可见操作'} ×</button>)}</div>
      <div className="mobile-favorite-list">{searchOperations.map((item) => <label className="mobile-favorite-item" key={item.id}>
        <Checkbox value={item.id} checked={favoriteDraft.includes(item.id)} disabled={saving} onChange={() => setFavoriteDraft((current) => current.includes(item.id) ? current.filter((id) => id !== item.id) : current.length < 20 ? [...current, item.id] : (mobileMessage.warning('最多选择20项常用操作'), current))} />
        <span><strong>{item.name}</strong><small>{item.source}{item.enabled ? '' : ' · 暂不可用'}</small></span>
      </label>)}</div>
      {props.catalogHasMore ? <button type="button" disabled={props.catalogLoading || saving} onClick={props.onLoadMore}>加载更多操作</button> : null}
    </Dialog>
  </div>;
}

export default MobileWorkbenchView;
